import React, { useEffect, useState } from 'react';
import { injectTheme, T } from '../theme/theme';
import { api } from '../API';
import AppHeader from '../common/AppHeader';

interface UserRow {
  id:         string;
  email:      string;
  full_name:  string;
  is_active:  boolean;
  is_admin:   boolean;
  created_at: string;
  last_login: string | null;
}

interface NewUserForm {
  email:     string;
  full_name: string;
  password:  string;
  is_admin:  boolean;
}

const EMPTY_FORM: NewUserForm = {
  email: '', full_name: '', password: '', is_admin: false,
};

function generatePassword(): string {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
  return Array.from({ length: 12 }, () =>
    chars[Math.floor(Math.random() * chars.length)]
  ).join('');
}

export default function AdminPage({ onClose }: { onClose: () => void }) {
  useEffect(() => { injectTheme(); }, []);

  const [users,   setUsers]   = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [form,    setForm]    = useState<NewUserForm>(EMPTY_FORM);
  const [success, setSuccess] = useState('');
  const [error,   setError]   = useState('');
  const [submitting, setSub]  = useState(false);
  const [createdPwd, setCreatedPwd] = useState('');
  const [copied,  setCopied]  = useState(false);

  useEffect(() => { fetchUsers(); }, []);

  async function fetchUsers() {
    setLoading(true);
    try {
      const res = await api.get('/adriva_api/auth/admin/users');
      setUsers(res.data.data || []);
    } catch {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }

  function handleAutoPassword() {
    const pwd = generatePassword();
    setForm(f => ({ ...f, password: pwd }));
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!form.email || !form.full_name || !form.password) {
      setError('All fields are required'); return;
    }
    setSub(true); setError(''); setSuccess('');
    try {
      await api.post('/adriva_api/auth/admin/users', form);
      setCreatedPwd(form.password);
      setSuccess(`User "${form.email}" created. Share the password below securely.`);
      setForm(EMPTY_FORM);
      fetchUsers();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to create user');
    } finally {
      setSub(false);
    }
  }

  async function handleToggle(userId: string, name: string) {
    try {
      await api.patch(`/adriva_api/auth/admin/users/${userId}/toggle`);
      setSuccess(`User "${name}" status updated`);
      fetchUsers();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to update user');
    }
  }

  async function handleResetPassword(email: string) {
    const newPwd = generatePassword();
    try {
      await api.post('/adriva_api/auth/admin/reset-user-password', {
        email, new_password: newPwd
      });
      setCreatedPwd(newPwd);
      setSuccess(`Password reset for "${email}". Share the new password below securely.`);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to reset password');
    }
  }

  function copyPwd() {
    navigator.clipboard.writeText(createdPwd);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const total  = users.length;
  const active = users.filter(u => u.is_active).length;
  const admins = users.filter(u => u.is_admin).length;

  return (
    <div style={{ background: T.bg, minHeight: '100vh' }}>
      <AppHeader
        onHome={onClose}
        actions={
          <button className="gw-btn gw-btn-ghost gw-btn-sm" onClick={onClose}>
            ← Back to app
          </button>
        }
      />

      <div className="gw-admin-page">
        <div style={{ marginBottom: 24 }}>
          <h1 className="gw-admin-page-title">User management</h1>
          <p className="gw-admin-page-sub">
            Create and manage user accounts. Share generated passwords
            securely — users can change them after first login.
          </p>
        </div>

        {/* Stats */}
        <div className="gw-admin-stat-strip">
          {[
            { val: total,  label: 'Total users'  },
            { val: active, label: 'Active'        },
            { val: admins, label: 'Admins'        },
          ].map(s => (
            <div key={s.label} className="gw-admin-stat">
              <div className="gw-admin-stat-val">{s.val}</div>
              <div className="gw-admin-stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Alerts */}
        {success && (
          <div className="gw-admin-success">
            <span>✓</span>
            <div>
              <div>{success}</div>
              {createdPwd && (
                <div className="gw-pwd-display">
                  <span>{createdPwd}</span>
                  <button
                    className="gw-btn gw-btn-outline gw-btn-sm"
                    onClick={copyPwd}
                    style={{ padding: '3px 10px', fontSize: 11 }}
                  >
                    {copied ? '✓ Copied' : 'Copy'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        {error && (
          <div className="gw-admin-error">
            <span>⚠</span> {error}
            <button
              style={{ marginLeft: 'auto', background: 'none', border: 'none',
                       cursor: 'pointer', color: T.red, fontSize: 16 }}
              onClick={() => setError('')}
            >×</button>
          </div>
        )}

        {/* Create user form */}
        <div className="gw-admin-form-card">
          <div className="gw-admin-form-hdr">
            <span>👤</span> Create new user
          </div>
          <div className="gw-admin-form-body">
            <form onSubmit={handleCreate}>
              <div className="gw-admin-form-row">
                <div className="gw-form-group">
                  <label className="gw-label">Full name</label>
                  <input
                    className="gw-input"
                    type="text"
                    placeholder="Jane Smith"
                    value={form.full_name}
                    onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
                    required
                  />
                </div>
                <div className="gw-form-group">
                  <label className="gw-label">Email address</label>
                  <input
                    className="gw-input"
                    type="email"
                    placeholder="jane@company.com"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    required
                  />
                </div>
              </div>

              <div className="gw-admin-form-row">
                <div className="gw-form-group">
                  <label className="gw-label">
                    Password
                    <button
                      type="button"
                      className="gw-btn gw-btn-ghost gw-btn-sm"
                      style={{ marginLeft: 8, padding: '2px 8px', fontSize: 10 }}
                      onClick={handleAutoPassword}
                    >
                      Auto-generate
                    </button>
                  </label>
                  <input
                    className="gw-input"
                    type="text"
                    placeholder="Min 8 characters"
                    value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    minLength={8}
                    required
                    style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }}
                  />
                </div>
                <div className="gw-form-group" style={{ justifyContent: 'center' }}>
                  <label className="gw-label">Role</label>
                  <label className="gw-checkbox-row" style={{ marginTop: 10 }}>
                    <input
                      type="checkbox"
                      className="gw-checkbox"
                      checked={form.is_admin}
                      onChange={e => setForm(f => ({ ...f, is_admin: e.target.checked }))}
                    />
                    <span className="gw-checkbox-label">Grant admin access</span>
                  </label>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button
                  type="button"
                  className="gw-btn gw-btn-ghost gw-btn-sm"
                  onClick={() => { setForm(EMPTY_FORM); setSuccess(''); setCreatedPwd(''); }}
                >
                  Clear
                </button>
                <button
                  type="submit"
                  className="gw-btn gw-btn-primary gw-btn-sm"
                  disabled={submitting}
                >
                  {submitting ? 'Creating…' : 'Create user →'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* User table */}
        <div className="gw-admin-form-card">
          <div className="gw-admin-form-hdr">
            <span>👥</span> All users
            <button
              className="gw-btn gw-btn-ghost gw-btn-sm"
              style={{ marginLeft: 'auto' }}
              onClick={fetchUsers}
            >
              ↻ Refresh
            </button>
          </div>

          {loading ? (
            <div style={{ padding: 32, textAlign: 'center', color: T.textMuted }}>
              Loading…
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="gw-user-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Last login</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id}>
                      <td style={{ fontWeight: 600 }}>{u.full_name}</td>
                      <td style={{ fontFamily: "'JetBrains Mono', monospace",
                                   fontSize: 12, color: T.textMuted }}>
                        {u.email}
                      </td>
                      <td>
                        <span className={`gw-badge ${u.is_admin ? 'gw-badge-brand' : 'gw-badge-muted'}`}>
                          {u.is_admin ? 'Admin' : 'User'}
                        </span>
                      </td>
                      <td>
                        <span className={`gw-badge ${u.is_active ? 'gw-badge-green' : 'gw-badge-red'}`}>
                          {u.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: T.textMuted }}>
                        {u.last_login
                          ? new Date(u.last_login).toLocaleDateString()
                          : '—'}
                      </td>
                      <td style={{ fontSize: 12, color: T.textMuted }}>
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            className={`gw-btn gw-btn-sm ${u.is_active ? 'gw-btn-danger' : 'gw-btn-success'}`}
                            onClick={() => handleToggle(u.id, u.full_name)}
                            style={{ fontSize: 11 }}
                          >
                            {u.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            className="gw-btn gw-btn-outline gw-btn-sm"
                            onClick={() => handleResetPassword(u.email)}
                            style={{ fontSize: 11 }}
                          >
                            Reset pwd
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}